import { type RefObject, useCallback, useLayoutEffect, useState } from 'react';

const SCROLL_MARGIN = 32;

/**
 * Fallback for calculations when an anchor is invalid
 * This would force the pinned viewport mode rather than having the card fallback to the left
 */
export const MISSING_BASE_ANCHOR_OFFSET = 99999;

/**
 * Fallback for the card's measured width, which must stay in sync with the `card`
 * style in `SuggestionCard.tsx`.
 *
 * `cardWidth` is read off the ref during render, so it is 0 on the first render after
 * the card mounts (the ref is only attached on commit) — and the horizontal observer
 * built from that render would use a right inset of just `SCROLL_MARGIN`, reporting
 * room on the right for any target with ≥32px to spare.
 */
const CARD_WIDTH_FALLBACK = 280;

type UseSuggestionCardPositioningProps = {
	cardRef: RefObject<HTMLDivElement>;
	id?: string;
	root?: Element;
	stickyHeaderOffset?: number;
	targetBase?: HTMLSpanElement;
	targetEnd?: HTMLSpanElement;
	targetStart?: HTMLSpanElement;
};

type UseSuggestionCardPositioningResult = {
	isCardBottomBelowViewport: boolean;
	isMeasured: boolean;
	isTargetAboveViewport: boolean;
	isTargetBelowViewport: boolean;
	isTargetEndAboveViewport: boolean;
	isTargetEndBelowViewport: boolean;
	/**
	 * The target was already wholly out of view when this suggestion was selected, as
	 * opposed to scrolling out of view while the card was open. Lets the consumer skip
	 * the card-to-scroll-back-button hand-off, which has nothing to hand off from.
	 */
	isTargetInitiallyOutsideViewport: boolean;
	isTargetRightOfViewport: boolean;
};

/**
 * Tracks the position of the suggestion card target relative to the editor viewport
 * using IntersectionObserver. Returns booleans describing where the target/card sit
 * relative to the viewport; the consumer positions the card off the CSS anchors.
 */
export const useSuggestionCardPositioning = ({
	id,
	root,
	targetStart,
	targetBase,
	targetEnd,
	cardRef,
	stickyHeaderOffset = 0,
}: UseSuggestionCardPositioningProps): UseSuggestionCardPositioningResult => {
	const [isTargetAboveViewport, setIsTargetAboveViewport] = useState(false);
	const [isTargetBelowViewport, setIsTargetBelowViewport] = useState(false);
	const [isCardBottomBelowViewport, setIsCardBottomBelowViewport] = useState(false);
	const [isTargetRightOfViewport, setIsTargetRightOfViewport] = useState(false);
	const [isTargetEndAboveViewport, setIsTargetEndAboveViewport] = useState(false);
	const [isTargetEndBelowViewport, setIsTargetEndBelowViewport] = useState(false);
	const [isTargetInitiallyOutsideViewport, setIsTargetInitiallyOutsideViewport] = useState(false);
	const [isMeasured, setIsMeasured] = useState(false);

	const cardHeight = cardRef.current?.offsetHeight ?? 0;
	// `|| CARD_WIDTH_FALLBACK` rather than `??` so a 0 from an unmounted (or
	// not-yet-laid-out) card also falls back instead of collapsing the inset.
	const cardWidth = cardRef.current?.offsetWidth || CARD_WIDTH_FALLBACK;

	const measure = useCallback((): boolean | null => {
		const card = cardRef.current;
		if (!id || !root || !targetStart || !targetEnd || !targetBase || !card) {
			return null;
		}

		const rootRect = root.getBoundingClientRect();
		// IntersectionObserver resolves an element root against its content-clip box —
		// the padding box minus scrollbars — which is what client* describes.
		const rootTop = rootRect.top + root.clientTop;
		const rootBottom = rootTop + root.clientHeight;
		const rootRight = rootRect.left + root.clientLeft + root.clientWidth;

		const height = card.offsetHeight;
		const width = card.offsetWidth || CARD_WIDTH_FALLBACK;

		const startRect = targetStart.getBoundingClientRect();
		const endRect = targetEnd.getBoundingClientRect();
		const baseRect = targetBase.getBoundingClientRect();

		// These mirror each observer's `rootMargin` below; keep the two in step.
		const targetAbove = startRect.top < rootTop + SCROLL_MARGIN + stickyHeaderOffset;
		const targetEndAbove = endRect.top < rootTop + stickyHeaderOffset;
		const targetBelow = startRect.top > rootBottom - SCROLL_MARGIN;
		const targetEndBelow = endRect.bottom > rootBottom - (height + SCROLL_MARGIN + 24);

		setIsTargetAboveViewport(targetAbove);
		setIsCardBottomBelowViewport(startRect.bottom > rootBottom - (height + SCROLL_MARGIN));
		setIsTargetBelowViewport(targetBelow);
		setIsTargetEndAboveViewport(targetEndAbove);
		setIsTargetEndBelowViewport(targetEndBelow);
		// With no base anchor there is nothing to align the card's inline start with, so fall back
		// to the right-constrained positioning used when there's no room on the right.
		const isBaseAnchorMissing = baseRect.right < rootRect.left - MISSING_BASE_ANCHOR_OFFSET / 2;
		setIsTargetRightOfViewport(
			isBaseAnchorMissing || baseRect.right > rootRight - (width + SCROLL_MARGIN),
		);

		const isOutsideViewport = (targetAbove && targetEndAbove) || (targetBelow && targetEndBelow);

		return isOutsideViewport;
	}, [id, root, targetStart, targetBase, targetEnd, cardRef, stickyHeaderOffset]);

	useLayoutEffect(() => {
		measure();

		const frame = requestAnimationFrame(() => {
			const isOutsideViewport = measure();
			if (isOutsideViewport === null) {
				return;
			}
			setIsTargetInitiallyOutsideViewport(isOutsideViewport);
			setIsMeasured(true);
		});
		return () => cancelAnimationFrame(frame);
	}, [measure]);

	useLayoutEffect(() => {
		if (!id || !root || !targetStart || !targetEnd || !cardRef.current || !targetBase) {
			setIsTargetRightOfViewport(false);
			setIsTargetEndAboveViewport(false);
			setIsTargetEndBelowViewport(false);
			setIsTargetBelowViewport(false);
			setIsTargetAboveViewport(false);
			setIsCardBottomBelowViewport(false);
			setIsTargetInitiallyOutsideViewport(false);
			setIsMeasured(false);
			return;
		}

		/**
		 * A notification may only *release* `isTargetInitiallyOutsideViewport` — as soon as the
		 * target is no longer wholly out of view, because the user scrolled back or a smooth
		 * scroll landed — so that a later scroll-away gets the normal card-to-button hand-off
		 * rather than the immediate swap. Re-arming here would make the flag indistinguishable
		 * from `shouldScrollUp || shouldScrollDown`.
		 */
		const notify = () => {
			if (measure() === false) {
				setIsTargetInitiallyOutsideViewport(false);
			}
		};

		const verticalCardPositionObserver = new IntersectionObserver(notify, {
			root,
			rootMargin: `-${stickyHeaderOffset + SCROLL_MARGIN}px 0px -${cardHeight + SCROLL_MARGIN}px 0px`,
		});

		const verticalTargetObserver = new IntersectionObserver(notify, {
			root,
			rootMargin: `${cardHeight + stickyHeaderOffset + SCROLL_MARGIN}px 0px -${SCROLL_MARGIN}px 0px`,
		});

		const targetEndObserver = new IntersectionObserver(notify, {
			root,
			// Extra 24 for the gap above card and content
			rootMargin: `-${stickyHeaderOffset}px 0px -${cardHeight + SCROLL_MARGIN + 24}px 0px`,
		});

		/**
		 * This observer is for determining if there's enough space on the right side to display the card.
		 * We use arbitrarily large root margins to ensure that the intersection observer detects it even
		 * when the targetBase anchor (anchored to the icon button) is off the screen.
		 *
		 * A hypotheical edge case is if a toolcall's localid position is more than 99999px further than the
		 * doc-level node's local id, but that is really unlikely.
		 */
		const horizontalObserver = new IntersectionObserver(notify, {
			root,
			rootMargin: `99999px -${cardWidth + SCROLL_MARGIN}px 99999px 0px`,
		});

		verticalCardPositionObserver.observe(targetStart);
		horizontalObserver.observe(targetBase);
		targetEndObserver.observe(targetEnd);
		verticalTargetObserver.observe(targetStart);
		return () => {
			verticalCardPositionObserver.disconnect();
			horizontalObserver.disconnect();
			targetEndObserver.disconnect();
			verticalTargetObserver.disconnect();
		};
	}, [
		targetStart,
		targetBase,
		cardRef,
		root,
		targetEnd,
		cardHeight,
		cardWidth,
		id,
		measure,
		stickyHeaderOffset,
	]);

	return {
		isTargetAboveViewport,
		isTargetBelowViewport,
		isCardBottomBelowViewport,
		isTargetRightOfViewport,
		isTargetEndAboveViewport,
		isTargetEndBelowViewport,
		isTargetInitiallyOutsideViewport,
		isMeasured,
	};
};
